import $ from 'jquery';

import { trackPaymentMethodSelection, trackPaymentSubmitSuccess } from 'chairisher/analytics/checkout';
import { getPurchaseObject, setPurchaseObject } from 'chairisher/context/purchase';
import { initializeBraintree, makeChairishShiptoAddress } from 'chairisher/thirdparty/braintreehelper';
import { handleAsyncError } from 'chairisher/util/logging';
import { getBraintreeClientToken, getPaymentProcessorCodes } from 'chairisher/context/payment';

const paypalCheckoutButtonSelector = '.js-paypal-express-checkout-button';

/**
 * Returns a new purchase object where the shipping partner code is null for each product.
 * This is needed because the initial shipping code may be invalid after express checkout changes the address.
 *
 * @returns {Object}
 */
function removeShippingPartnerCodeFromPurchaseObject(purchaseObject) {
    return {
        ...purchaseObject,
        products: purchaseObject.products.map((product) => ({
            ...product,
            shipping_partner_code: { value: null },
        })),
    };
}

/**
 * Manages the express checkout buttons on the cart page.
 */
class ExpressCheckoutView {
    constructor() {
        /**
         * Callback that completes all checkout sections and makes the final section available.
         */
        this.expressCheckoutCallback = null;
    }

    initialize() {
        if ($(paypalCheckoutButtonSelector).length) {
            this.initializePaypal(); // If button is in the DOM, load Paypal first - button is invisible until it completes.
        }
    }

    /**
     * @returns {jQuery}
     */
    getPaymentRequestButtonWrapper() {
        return $('.js-express-payment-request-button-wrapper');
    }

    /**
     * Sets the function that should be called after the user is redirected back to cart from a 3rd party form.
     *
     * @param {function} callback
     */
    setExpressCheckoutCallback(callback) {
        this.expressCheckoutCallback = callback;
    }

    /**
     * Initializes the 3rdparty BrainTree and PayPal JS SDKs
     */
    initializePaypal() {
        let braintreeDeviceData;

        initializeBraintree({
            braintreeToken: getBraintreeClientToken(),
            setDeviceData: (deviceData) => {
                braintreeDeviceData = deviceData;
            },
            onFail: () => $(paypalCheckoutButtonSelector).remove(),
            checkoutCallback: (paypal, checkout) => {
                paypal
                    .Buttons({
                        fundingSource: paypal.FUNDING.PAYPAL,
                        style: {
                            color: 'blue',
                            height: 36,
                        },
                        createBillingAgreement: () =>
                            checkout.createPayment({
                                flow: 'vault',
                                enableShippingAddress: true,
                            }),
                        onApprove: (data) =>
                            checkout
                                .tokenizePayment(data)
                                .then(
                                    (payload) => this.onPaypalApprove(payload, braintreeDeviceData),
                                    handleAsyncError,
                                ),
                    })
                    .render(paypalCheckoutButtonSelector);
            },
        });
    }

    /**
     * Sets Paypal data to the purchase context.
     *
     * @param {Object} payload The payload returned by Paypal's hosted form
     * @param {Object} deviceData The device data returned by the loader
     */
    onPaypalApprove(payload, deviceData) {
        const purchaseObject = removeShippingPartnerCodeFromPurchaseObject(getPurchaseObject());

        if (!purchaseObject.account.email.value) {
            purchaseObject.account.email.value = payload.details.email;
        }

        if (payload.details.shippingAddress) {
            purchaseObject.shipto_address = makeChairishShiptoAddress(payload);
        }
        purchaseObject.payment_method = {
            braintree_device_data: deviceData,
            braintree_nonce: { value: payload.nonce },
        };
        setPurchaseObject(purchaseObject);

        const braintreeCode = getPaymentProcessorCodes().BRAINTREE;
        trackPaymentMethodSelection(braintreeCode);
        trackPaymentSubmitSuccess(braintreeCode);

        this.expressCheckoutCallback();
    }
}

export default ExpressCheckoutView;
